import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import apiInstance from "../../instances/apiInstance";
import { IdNameList } from "../common/models";
import {
  CoachReportParams,
  CoachReportSummary,
  NCBPReportsParams,
  NCBPReportsResponseSummary,
  NotificationStatusReportSummary,
  NotificationStatusSummaryParams,
  OutreachReportParams,
  OutreachReportSummary,
  PendingBillsSummary,
  PendingBillsSummaryParams,
} from "./models";

export const useGetPendingBillingReports = () => {
  const [state, setState] = useState<{
    isFiltered: boolean;
    filterParams: PendingBillsSummaryParams;
  }>({
    isFiltered: false,
    filterParams: {} as PendingBillsSummaryParams,
  });
  const mutation = useMutation<
    AxiosResponse<PendingBillsSummary[]>,
    Error,
    PendingBillsSummaryParams | undefined
  >({
    mutationFn: (
      params?: PendingBillsSummaryParams,
    ): Promise<AxiosResponse<PendingBillsSummary[]>> => {
      return apiInstance.post(API_ENDPOINTS.PENDING_BILLING_REPORTS, {
        ...params,
      });
    },
    onSuccess: (_data, variables) => {
      setState((prevState) => ({
        ...prevState,
        isFiltered: !!variables?.clientIds?.length,
        filterParams: variables as PendingBillsSummaryParams,
      }));
    },
  });
  return {
    ...mutation,
    ...state,
  };
};

export const useGetNotificationStatusReports = () => {
  const [state, setState] = useState<{
    isFiltered: boolean;
    filterParams: NotificationStatusSummaryParams;
  }>({
    isFiltered: false,
    filterParams: {},
  });
  const mutation = useMutation<
    AxiosResponse<NotificationStatusReportSummary[]>,
    Error,
    NotificationStatusSummaryParams | undefined
  >({
    mutationFn: (
      params?: NotificationStatusSummaryParams,
    ): Promise<AxiosResponse<NotificationStatusReportSummary[]>> => {
      return apiInstance.post(API_ENDPOINTS.NOTIFICATION_STATUS_REPORTS, {
        ...params,
      });
    },
    onSuccess: (_data, variables) => {
      setState((prevState) => ({
        ...prevState,
        isFiltered: !!variables?.marketIds?.length,
        filterParams: variables as NotificationStatusSummaryParams,
      }));
    },
  });
  return {
    ...mutation,
    ...state,
  };
};

export const useExportNotificationStatusReport = () => {
  return useMutation<
    AxiosResponse<Blob>,
    Error,
    NotificationStatusSummaryParams
  >({
    mutationFn: (
      params: NotificationStatusSummaryParams,
    ): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(
        API_ENDPOINTS.EXPORT_NOTIFICATION_STATUS_REPORT,
        {
          ...params,
        },
        {
          responseType: "blob",
        },
      );
    },
  });
};

export const useGetOutreachReports = () => {
  const [state, setState] = useState<{
    isFiltered: boolean;
    filterParams: OutreachReportParams;
  }>({
    isFiltered: false,
    filterParams: {},
  });
  const mutation = useMutation<
    AxiosResponse<OutreachReportSummary[]>,
    Error,
    OutreachReportParams | undefined
  >({
    mutationFn: (
      params?: OutreachReportParams,
    ): Promise<AxiosResponse<OutreachReportSummary[]>> => {
      return apiInstance.post(API_ENDPOINTS.OUTREACH_REPORTS, {
        ...params,
      });
    },
    onSuccess: (_data, variables) => {
      setState((prevState) => ({
        ...prevState,
        isFiltered: !!variables?.marketIds?.length,
        filterParams: variables as OutreachReportParams,
      }));
    },
  });
  return {
    ...mutation,
    ...state,
  };
};

export const useExportOutreachReport = () => {
  return useMutation<AxiosResponse<Blob>, Error, OutreachReportParams>({
    mutationFn: (
      params: OutreachReportParams,
    ): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(
        API_ENDPOINTS.EXPORT_OUTREACH_REPORT,
        {
          ...params,
        },
        {
          responseType: "blob",
        },
      );
    },
  });
};

export const useGetReportSearchCategory = () => {
  return useMutation<AxiosResponse<IdNameList[]>, Error>({
    mutationFn: (): Promise<AxiosResponse<IdNameList[]>> => {
      return apiInstance.get(API_ENDPOINTS.REPORT_SEARCH_CATEGORY);
    },
  });
};

export const useGetReportSearchType = () => {
  return useMutation<
    AxiosResponse<IdNameList[]>,
    Error,
    { categoryId: string }
  >({
    mutationFn: ({ categoryId }): Promise<AxiosResponse<IdNameList[]>> => {
      return apiInstance.get(
        API_ENDPOINTS.getReportTypesByCategoryId(categoryId),
      );
    },
  });
};

export const useGetAllMarketForDeliveryOffices = () => {
  return useQuery({
    queryKey: ["GetAllMarketForDeliveryOffices"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_ALL_MARKET_FOR_DELIVERY_OFFICES,
      );
      return res.data as IdNameList[];
    },
    retry: false,
  });
};

export const useExportPendingBillingSummeryReport = () => {
  return useMutation<AxiosResponse<Blob>, Error, PendingBillsSummaryParams>({
    mutationFn: (
      params: PendingBillsSummaryParams,
    ): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(
        API_ENDPOINTS.EXPORT_PENDING_BILLING_REPORT,
        {
          ...params,
        },
        {
          responseType: "blob",
        },
      );
    },
  });
};

export const useGetPowerBiReports = (isUserSpecificList: boolean = false) => {
  return useQuery({
    queryKey: ["GetPowerBiReports"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_POWER_BI_REPORTS(isUserSpecificList),
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetCoachReports = () => {
  const [state, setState] = useState<{
    isFiltered: boolean;
    filterParams: CoachReportParams;
  }>({
    isFiltered: false,
    filterParams: {},
  });
  const mutation = useMutation<
    AxiosResponse<CoachReportSummary[]>,
    Error,
    CoachReportParams | undefined
  >({
    mutationFn: (
      params?: CoachReportParams,
    ): Promise<AxiosResponse<CoachReportSummary[]>> => {
      return apiInstance.post(API_ENDPOINTS.GET_COACH_REPORT, {
        ...params,
      });
    },
    onSuccess: (_data, variables) => {
      setState((prevState) => ({
        ...prevState,
        isFiltered: !!variables?.coachIds?.length,
        filterParams: variables as CoachReportParams,
      }));
    },
  });
  return {
    ...mutation,
    ...state,
  };
};

export const useExportCoachReport = () => {
  return useMutation<AxiosResponse<Blob>, Error, CoachReportParams>({
    mutationFn: (params: CoachReportParams): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(
        API_ENDPOINTS.EXPORT_COACH_REPORT,
        {
          ...params,
        },
        {
          responseType: "blob",
        },
      );
    },
  });
};

export const useGetNCBPReports = () => {
  const [state, setState] = useState<{
    filterParams: NCBPReportsParams;
  }>({
    filterParams: {} as NCBPReportsParams,
  });
  const mutation = useMutation<
    AxiosResponse<NCBPReportsResponseSummary[]>,
    Error,
    NCBPReportsParams | undefined
  >({
    mutationFn: (
      params?: NCBPReportsParams,
    ): Promise<AxiosResponse<NCBPReportsResponseSummary[]>> => {
      return apiInstance.post(API_ENDPOINTS.NCBP_REPORT, {
        ...params,
      });
    },
    onSuccess: (_data, variables) => {
      setState((prevState) => ({
        ...prevState,
        filterParams: variables as NCBPReportsParams,
      }));
    },
  });
  return {
    ...mutation,
    ...state,
  };
};

export const useExportNCBPReport = () => {
  return useMutation<AxiosResponse<Blob>, Error, NCBPReportsParams>({
    mutationFn: (params: NCBPReportsParams): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(API_ENDPOINTS.EXPORT_NCBP_REPORT, params, {
        responseType: "blob",
      });
    },
  });
};